import { existsSync, promises as fs } from 'node:fs';
import path from 'node:path';
import { containerConfigPath, monocerosHome } from '../config/paths.js';
import { readConfig } from '../config/io.js';
import { solutionConfigToCreateOptions } from '../config/transform.js';
import { listApps, readLaunchConfig } from '../config/launch-config.js';
import { loadComponentCatalog } from '../init/components.js';
import { loadFeatureManifestSummary } from '../init/manifest.js';
import { knownLanguages, knownServices } from '../create/catalog.js';
import { PROVIDER_VALUES, REGEX } from '../config/schema.js';
import { OPEN_TOOLS } from '../open/index.js';

/**
 * Shell-agnostic completion engine. Called by the `__complete`
 * internal CLI command and (in tests) directly via `resolveCompletions`.
 *
 * The caller hands us the full command-line buffer (`line`) plus the
 * cursor's byte offset within it (`point`). We tokenize the prefix,
 * figure out which positional / flag is being completed, and return
 * a list of candidate completions matching the current token fragment.
 *
 * Tokenization rules:
 *   - Whitespace separates tokens.
 *   - Double or single quotes group a run including whitespace; we
 *     return the un-quoted content as the token (the shell glue
 *     re-quotes when emitting).
 *   - A `--key=value` form is one token; the equals sign isn't a
 *     separator. Inside `--with-features=github,claude` we treat the part
 *     after `=` as the current value-fragment for the `--with-features` flag.
 *
 * Per-command suggestions live in `COMMAND_SPECS`. Adding a new
 * command means adding (or extending) one entry there — the engine
 * dispatches automatically.
 *
 * Source of truth is citty's command definitions; the spec table is
 * a thin mirror keyed by command name. A test (`completion.test.ts`)
 * pins the spec keys against the live command list.
 */

// ─── Public surface ───────────────────────────────────────────────

export interface ResolveOptions {
  /** Override of MONOCEROS_HOME (tests). */
  monocerosHome?: string;
}

export async function resolveCompletions(
  line: string,
  point: number,
  opts: ResolveOptions = {},
): Promise<string[]> {
  const { prev, current } = parseCompletionLine(line, point);
  const ctx: Ctx = { prev, current, opts };
  // prev[0] is the program name (usually "monoceros"); skip it.
  const head = prev[0];
  const afterProgram = head === undefined ? [] : prev.slice(1);
  // No subcommand typed yet → suggest the subcommand list.
  if (afterProgram.length === 0) {
    return filterPrefix(ALL_COMMANDS, current);
  }
  const command = afterProgram[0]!;
  const spec = COMMAND_SPECS[command];
  if (!spec) {
    // Unknown subcommand. We've already entered position 2+, but the
    // command isn't one we know — no useful suggestions.
    return [];
  }
  // Tokens that belong to the command's args (past `monoceros <cmd>`).
  const argTokens = afterProgram.slice(1);
  return dispatchCommand(spec, argTokens, ctx);
}

// ─── Tokenizer + cursor-context parser ────────────────────────────

interface CompletionContext {
  /** Tokens to the LEFT of the one being completed. */
  prev: string[];
  /** The token currently under the cursor (or `''` at a fresh word). */
  current: string;
}

interface Ctx extends CompletionContext {
  opts: ResolveOptions;
}

export function parseCompletionLine(
  line: string,
  point: number,
): CompletionContext {
  const before = line.slice(0, Math.max(0, Math.min(point, line.length)));
  const tokens = tokenize(before);
  // If the cursor sits right after whitespace, the user is starting a
  // fresh token — current is empty, all preceding tokens are "prev".
  const lastChar = before.length > 0 ? before[before.length - 1]! : '';
  if (tokens.length === 0 || isShellWhitespace(lastChar)) {
    return { prev: tokens, current: '' };
  }
  return {
    prev: tokens.slice(0, -1),
    current: tokens[tokens.length - 1]!,
  };
}

function tokenize(text: string): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < text.length) {
    while (i < text.length && isShellWhitespace(text[i]!)) i++;
    if (i >= text.length) break;
    let token = '';
    let quote: '"' | "'" | null = null;
    while (i < text.length) {
      const ch = text[i]!;
      if (quote === null && isShellWhitespace(ch)) break;
      if (quote === null && (ch === '"' || ch === "'")) {
        quote = ch;
        i++;
        continue;
      }
      if (quote !== null && ch === quote) {
        quote = null;
        i++;
        continue;
      }
      token += ch;
      i++;
    }
    out.push(token);
  }
  return out;
}

function isShellWhitespace(ch: string): boolean {
  return ch === ' ' || ch === '\t';
}

// ─── Per-command dispatch ─────────────────────────────────────────

type ValueSource = (ctx: Ctx) => Promise<string[]> | string[];

// ─── PowerShell static-model tagging ──────────────────────────────
//
// The bash/zsh wrappers call back into `monoceros __complete` on every
// Tab, so their value sources run live. On Windows the CLI lives inside
// a WSL distro reached through a `.cmd` shim, so a per-Tab callback is a
// full WSL round-trip — far too slow. Instead the pwsh script is
// generated once (at install time) as a self-contained completer: it
// bakes the STATIC candidate lists in and resolves the few dynamic ones
// (container names, apps, workspace dirs, launch targets) directly off
// the host filesystem — everything is reachable via the
// `%USERPROFILE%\.monoceros` symlink, no CLI call needed.
//
// To emit that model we tag each value source as either `static`
// (bakeable now) or `dynamic` with a `kind` the pwsh script knows how to
// resolve from the filesystem. Untagged sources (freeform value flags,
// the `upgrade` version slot, feature-option inner-args) get no pwsh
// suggestions. Tagging keeps `COMMAND_SPECS` the single source of truth:
// `buildPwshCompletionModel()` derives the whole model from it.

/** Dynamic value kinds the pwsh completer resolves host-side. */
export type PwshValueKind =
  | 'containerName'
  | 'app'
  | 'appOrService'
  | 'runInDir'
  | 'target';

type PwshMeta = { static: true } | { static: false; kind: PwshValueKind };
const pwshMeta = new WeakMap<object, PwshMeta>();

/** Tag a source whose values can be baked into the pwsh script now. */
function staticSource(fn: ValueSource): ValueSource {
  pwshMeta.set(fn, { static: true });
  return fn;
}

/** Tag a source the pwsh script resolves from the filesystem at Tab time. */
function dynamicSource(kind: PwshValueKind, fn: ValueSource): ValueSource {
  pwshMeta.set(fn, { static: false, kind });
  return fn;
}

interface FlagSpec {
  /** `boolean` = no value; `value` = `--flag <X>` or `--flag=<X>`. */
  type: 'boolean' | 'value';
  /** Short forms, e.g. `['-y']`. */
  aliases?: string[];
  /** Value suggestions for `value`-typed flags. Optional → freeform. */
  values?: ValueSource;
}

interface CommandSpec {
  /**
   * Suggestion sources for positional args, indexed by position
   * (0 = arg right after the command name). Missing entries mean
   * the slot exists but has no completion source (e.g. `init`'s
   * fresh-name positional — we don't suggest existing container
   * names there, that would invite collisions).
   */
  positionals?: ValueSource[];
  /**
   * How many positionals the command expects. Defaults to
   * `positionals.length`. Set this explicitly when the command has
   * MORE positional slots than entries in `positionals` (= "this
   * slot exists but has no suggestion source"). Once the cursor sits
   * past `positionalCount`, completion falls back to flag names so
   * the builder discovers `--with-*` / `--yes` etc. via Tab.
   */
  positionalCount?: number;
  /** Flag table. Keys include the leading `--`. */
  flags?: Record<string, FlagSpec>;
  /**
   * Suggestion source for tokens after `--` (inner args). Used by
   * `add-feature -- key=value` and `add-apt-packages -- pkg pkg …`.
   */
  innerArgs?: ValueSource;
}

function dispatchCommand(
  spec: CommandSpec,
  argTokens: string[],
  ctx: Ctx,
): Promise<string[]> | string[] {
  // Split argTokens at the `--` separator: tokens after it are inner
  // args (consumed by `innerArgs`); tokens before it are positionals
  // and flags consumed by `resolvePreDash`. We only need preDash —
  // the post-dash tokens are read directly off ctx.prev by per-command
  // innerArgs sources that care (see listFeatureOptionInnerArgs).
  const dashDashIdx = argTokens.indexOf('--');
  const preDash = dashDashIdx < 0 ? argTokens : argTokens.slice(0, dashDashIdx);
  const inPostDash = dashDashIdx >= 0;

  if (inPostDash && spec.innerArgs) {
    return resolveValues(spec.innerArgs, ctx, ctx.current);
  }

  // Post-dash but no inner-arg source (e.g. `run`, where everything
  // after `--` is a freeform inner command): suggest nothing and let the
  // shell complete the inner command itself. Without this guard the
  // command's own flags (e.g. run's `--in`) would leak into the
  // inner-command slot.
  if (inPostDash) return [];

  // Pre-dash: figure out whether `current` is a value for a flag we
  // started typing, or a fresh positional / flag-name slot.
  return resolvePreDash(spec, preDash, ctx);
}

async function resolvePreDash(
  spec: CommandSpec,
  preDash: string[],
  ctx: Ctx,
): Promise<string[]> {
  const current = ctx.current;

  // Case A: current looks like `--flag=…` → suggest values for that flag.
  if (current.startsWith('--') && current.includes('=')) {
    const eqIdx = current.indexOf('=');
    const flagName = current.slice(0, eqIdx);
    const valueFragment = current.slice(eqIdx + 1);
    const flag = spec.flags?.[flagName];
    if (!flag || flag.type !== 'value' || !flag.values) return [];
    const completions = await resolveValues(flag.values, ctx, valueFragment);
    // Re-emit with the `--flag=` prefix attached so the shell inserts
    // the full token in place.
    return completions.map((v) => `${flagName}=${v}`);
  }

  // Case B: current starts with `-` (incomplete flag name).
  if (current.startsWith('-')) {
    return listFlagNames(spec.flags ?? {}, current);
  }

  // Case C: previous token was `--flag` (no `=`) expecting a value.
  const lastPrev = preDash[preDash.length - 1];
  if (lastPrev && lastPrev.startsWith('--') && !lastPrev.includes('=')) {
    const flag = spec.flags?.[lastPrev];
    if (flag && flag.type === 'value' && flag.values) {
      return resolveValues(flag.values, ctx, current);
    }
  }

  // Case D: positional. Count how many positionals have been completed
  // already — that's any preDash token that isn't itself a flag or a
  // flag-value pair we passed through.
  const positionalIdx = countCompletedPositionals(preDash, spec.flags ?? {});
  const positionals = spec.positionals ?? [];
  const expectedPositionalCount = spec.positionalCount ?? positionals.length;

  // Still inside a defined positional slot → use its source.
  if (positionalIdx < positionals.length) {
    const positional = positionals[positionalIdx];
    if (positional) return resolveValues(positional, ctx, current);
  }
  // Past all expected positionals → surface available flags so Tab
  // discovers them without the builder having to know they exist
  // (and without having to start with a `-`).
  if (positionalIdx >= expectedPositionalCount) {
    return listFlagNames(spec.flags ?? {}, current);
  }
  // Slot is expected but has no completion source (e.g. `init`'s
  // fresh-name positional, `restore`'s backup-path). Don't suggest
  // anything — let the shell fall through to its built-in handling.
  return [];
}

/**
 * Flag-name suggestion list filtered against the partial token under
 * the cursor. Includes long names (`--with-features`) and short aliases (`-y`).
 *
 * Value-flags are emitted WITH a trailing `=` (`--with-features=`) so the shell
 * wrappers can use `compopt -o nospace` (bash) / `compadd -S ''` (zsh)
 * to suppress the auto-added trailing space — without that, picking
 * `--with-ports` via Tab and typing `=3000` afterwards produces the
 * broken `--with-ports =3000` (space between flag and value).
 *
 * Boolean flags (no value expected) stay as bare names so the shell's
 * normal trailing-space behaviour applies — after `--yes` you really
 * do want a space.
 */
function listFlagNames(
  flags: Record<string, FlagSpec>,
  fragment: string,
): string[] {
  const names: string[] = [];
  for (const [name, spec] of Object.entries(flags)) {
    names.push(spec.type === 'value' ? `${name}=` : name);
    for (const alias of spec.aliases ?? []) names.push(alias);
  }
  return filterPrefix(names, fragment);
}

function countCompletedPositionals(
  preDash: string[],
  flags: Record<string, FlagSpec>,
): number {
  let count = 0;
  for (let i = 0; i < preDash.length; i++) {
    const t = preDash[i]!;
    if (t.startsWith('--') && t.includes('=')) continue; // `--flag=value` — a flag, not a positional
    if (t.startsWith('-')) {
      // Boolean flag → 1 token, value flag → 2 tokens (consume next).
      const flag = flags[t] ?? aliasFlag(flags, t);
      if (flag && flag.type === 'value' && i + 1 < preDash.length) {
        i++; // skip the value
      }
      continue;
    }
    count++;
  }
  return count;
}

function aliasFlag(
  flags: Record<string, FlagSpec>,
  token: string,
): FlagSpec | undefined {
  for (const f of Object.values(flags)) {
    if (f.aliases?.includes(token)) return f;
  }
  return undefined;
}

async function resolveValues(
  source: ValueSource,
  ctx: Ctx,
  fragment: string,
): Promise<string[]> {
  const values = await source(ctx);
  // For comma-separated value flags (`--with-features=github,claude`) the
  // user may have typed `github,cl` — the fragment to filter against is the
  // part AFTER the last comma.
  const commaIdx = fragment.lastIndexOf(',');
  if (commaIdx < 0) {
    return filterPrefix(values, fragment);
  }
  const prefix = fragment.slice(0, commaIdx + 1);
  const tail = fragment.slice(commaIdx + 1);
  return filterPrefix(values, tail).map((v) => `${prefix}${v}`);
}

function filterPrefix(values: readonly string[], fragment: string): string[] {
  if (fragment.length === 0) return [...values];
  return values.filter((v) => v.startsWith(fragment));
}

// ─── Value sources ────────────────────────────────────────────────

async function listContainerNames(ctx: Ctx): Promise<string[]> {
  const home = ctx.opts.monocerosHome ?? monocerosHome();
  const dir = path.join(home, 'container-configs');
  if (!existsSync(dir)) return [];
  const entries = await fs.readdir(dir);
  return entries
    .filter((e) => e.endsWith('.yml'))
    .map((e) => e.slice(0, -'.yml'.length))
    .sort();
}

/**
 * The container name a lifecycle command is operating on, read from the
 * tokens already on the line. Every such command takes the name as its
 * first positional (`monoceros <cmd> <name> …`), so we walk the tokens
 * after the program + command and return the first non-flag one. Flag
 * tokens (`--in`, `--yes`, `--in=foo`) are skipped. Returns `undefined`
 * when the name hasn't been typed yet (e.g. completing `--in` before the
 * name) — callers then have nothing to enumerate against.
 */
function containerNameFromCtx(ctx: Ctx): string | undefined {
  // ctx.prev[0] is the program, [1] is the command; the name follows.
  for (let i = 2; i < ctx.prev.length; i++) {
    const t = ctx.prev[i]!;
    if (t === '--') break;
    if (t.startsWith('-')) continue;
    return t;
  }
  return undefined;
}

/** The n-th positional token (skipping flags) after `monoceros <cmd>`. */
function positionalFromCtx(ctx: Ctx, n: number): string | undefined {
  const positionals: string[] = [];
  for (let i = 2; i < ctx.prev.length; i++) {
    const t = ctx.prev[i]!;
    if (t === '--') break;
    if (t.startsWith('-')) continue;
    positionals.push(t);
  }
  return positionals[n];
}

/**
 * App candidates for `start/stop/logs <name> <app>` — paths under
 * `projects/` carrying a launch config. Pure host-side filesystem walk
 * (works with the container stopped), context-sensitive on the container
 * name already on the line.
 */
async function listAppCandidates(ctx: Ctx): Promise<string[]> {
  const name = containerNameFromCtx(ctx);
  if (!name) return [];
  try {
    return await listApps(name, ctx.opts.monocerosHome);
  } catch {
    return [];
  }
}

/**
 * Second-positional candidates for `status <name> <app|service>`: the apps
 * (launch configs under `projects/`) plus the services the yml declares -
 * exactly the two things that positional narrows to. Host-side and
 * best-effort: a missing/unreadable yml just drops the service half.
 */
async function listAppOrServiceCandidates(ctx: Ctx): Promise<string[]> {
  const name = containerNameFromCtx(ctx);
  if (!name) return [];
  const apps = await listApps(name, ctx.opts.monocerosHome).catch(() => []);
  let services: string[] = [];
  try {
    const parsed = await readConfig(
      containerConfigPath(name, ctx.opts.monocerosHome),
    );
    services = solutionConfigToCreateOptions(parsed.config).services.map(
      (s) => s.name,
    );
  } catch {
    // no yml, or it doesn't parse — offer apps only
  }
  return [...new Set([...apps, ...services])].sort();
}

/**
 * `--target` candidates: the config names from the already-typed app's
 * launch config (positional 1, after the container name at positional 0).
 */
async function listTargetCandidates(ctx: Ctx): Promise<string[]> {
  const name = containerNameFromCtx(ctx);
  const app = positionalFromCtx(ctx, 1);
  if (!name || !app) return [];
  try {
    const cfg = await readLaunchConfig(name, app, ctx.opts.monocerosHome);
    return cfg ? cfg.configurations.map((t) => t.name) : [];
  } catch {
    return [];
  }
}

/** Directory names never worth offering as a completion target. */
const WORKSPACE_DIR_SKIP = new Set(['node_modules']);
/**
 * How deep to walk under `projects/`. Cloned apps sit at `projects/<app>`
 * (depth 1) and their own source dirs a level or two below — 4 covers
 * those without recursing into deep dependency trees.
 */
const PROJECTS_DIR_MAX_DEPTH = 4;

/**
 * Recursively collect directory paths under `dir`, relative to it and
 * capped at `maxDepth` levels. Dot-directories (`.git`, `.devcontainer`,
 * …) and `node_modules` are skipped as noise. Symlinks are not followed
 * (`isDirectory()` is false for them), so a self-referential link can't
 * loop. Returns `[]` for a missing / unreadable directory.
 *
 * The shared host-side enumeration behind `run --in` completion (and the
 * `projects/<app>` completion in #13).
 */
async function collectDirs(dir: string, maxDepth: number): Promise<string[]> {
  const out: string[] = [];
  async function walk(at: string, rel: string, depth: number): Promise<void> {
    let entries;
    try {
      entries = await fs.readdir(at, { withFileTypes: true });
    } catch {
      return; // unreadable / missing dir — nothing to add
    }
    for (const e of entries) {
      if (!e.isDirectory()) continue;
      if (e.name.startsWith('.')) continue;
      if (WORKSPACE_DIR_SKIP.has(e.name)) continue;
      const childRel = rel ? `${rel}/${e.name}` : e.name;
      out.push(childRel);
      if (depth + 1 < maxDepth) {
        await walk(path.join(at, e.name), childRel, depth + 1);
      }
    }
  }
  await walk(dir, '', 0);
  return out;
}

/**
 * Directory paths inside a materialized container's workspace, relative
 * to the workspace root, for `run --in <dir>` completion. The workspace
 * root is `<MONOCEROS_HOME>/container/<name>/`, bind-mounted into the
 * container at `/workspaces/<name>` — so a host-side walk yields exactly
 * the relative paths `--in` accepts, and works with the container
 * stopped.
 *
 * Scope mirrors how the workspace is used: top-level dirs one level deep
 * (so service-data trees under e.g. `data/` don't flood the list), plus
 * a deep walk under `projects/`, where cloned apps and their source dirs
 * live.
 */
async function listContainerWorkspaceDirs(
  home: string,
  name: string,
): Promise<string[]> {
  const root = path.join(home, 'container', name);
  const top = await collectDirs(root, 1);
  const projects = (
    await collectDirs(path.join(root, 'projects'), PROJECTS_DIR_MAX_DEPTH)
  ).map((p) => `projects/${p}`);
  return [...new Set([...top, ...projects])].sort();
}

async function listRunInDirs(ctx: Ctx): Promise<string[]> {
  const name = containerNameFromCtx(ctx);
  if (!name) return [];
  const home = ctx.opts.monocerosHome ?? monocerosHome();
  return listContainerWorkspaceDirs(home, name);
}

async function listFeatureComponents(): Promise<string[]> {
  const catalog = await loadComponentCatalog();
  return [...catalog.values()]
    .filter((c) => c.file.category === 'feature')
    .map((c) => c.name)
    .sort();
}

async function listMcpServers(): Promise<string[]> {
  const catalog = await loadComponentCatalog();
  return [...catalog.values()]
    .filter((c) => c.file.category === 'mcp-server')
    .map((c) => c.name)
    .sort();
}

function listLanguageNames(): string[] {
  return knownLanguages().sort();
}

function listServiceNames(): string[] {
  return knownServices().sort();
}

function listProviders(): string[] {
  return [...PROVIDER_VALUES];
}

function listShellNames(): string[] {
  return ['bash', 'zsh', 'pwsh'];
}

/**
 * Inner-arg completion for `monoceros add-feature <name> <feature> --
 * key=value …`. The `<feature>` token can be either a catalog short
 * name (`atlassian`, `atlassian/twg`) or a full OCI ref; in both cases
 * we resolve to the feature manifest and return the option keys.
 *
 * Behaviour with the current token:
 *   - Token is empty or contains no `=` → suggest the option NAMES
 *     (filtered against the partial token prefix).
 *   - Token is `<key>=<fragment>` AND the key is a boolean → suggest
 *     `<key>=true` / `<key>=false` matching `<fragment>`. For string
 *     options we have no useful default suggestion list (it's
 *     freeform credentials / URLs).
 *   - Already-typed `key=value` pairs (before the current token) drop
 *     the same `key=` from the suggestion list so the builder doesn't
 *     get duplicates.
 */
async function listFeatureOptionInnerArgs(ctx: Ctx): Promise<string[]> {
  // Locate the feature token. ctx.prev[0] is the program name, [1] is
  // `add-feature`, [2] is the container name, [3] is the feature.
  // The user could have added flags like `--yes` before the feature,
  // but flag tokens always start with `-`. So the feature is the
  // SECOND non-flag positional after the command.
  const after = ctx.prev.slice(2); // drop "monoceros", "add-feature"
  let positionalCount = 0;
  let featureToken: string | undefined;
  for (let i = 0; i < after.length; i++) {
    const t = after[i]!;
    if (t === '--') break; // stop at the inner-args separator
    if (t.startsWith('-')) continue; // flag
    positionalCount++;
    if (positionalCount === 2) {
      featureToken = t;
      break;
    }
  }
  if (!featureToken) return [];
  const ref = await resolveFeatureRefForCompletion(featureToken);
  if (!ref) return [];
  const summary = loadFeatureManifestSummary(ref);
  if (!summary) return [];

  // Which keys has the builder already set in earlier inner-args?
  const dashDash = ctx.prev.indexOf('--');
  const innerSoFar = dashDash >= 0 ? ctx.prev.slice(dashDash + 1) : [];
  const usedKeys = new Set<string>();
  for (const t of innerSoFar) {
    const eq = t.indexOf('=');
    if (eq > 0) usedKeys.add(t.slice(0, eq));
  }

  // Current token: `key=value` or just `key`?
  const eqIdx = ctx.current.indexOf('=');
  if (eqIdx >= 0) {
    const key = ctx.current.slice(0, eqIdx);
    const valueFragment = ctx.current.slice(eqIdx + 1);
    const type = summary.optionTypes[key];
    if (type === 'boolean') {
      return ['true', 'false']
        .filter((v) => v.startsWith(valueFragment))
        .map((v) => `${key}=${v}`);
    }
    // String options: no useful suggestion list. Return [] so the
    // shell falls back to its own filename / nothing handling.
    return [];
  }

  // Plain key fragment — suggest the still-unused option keys WITH a
  // trailing `=` so the shell wrappers' nospace logic kicks in (same
  // shape as `--with-features=` etc. on the flag side). Without that, the
  // user gets `instance =foo` instead of `instance=foo` after Tab +
  // manual `=foo`.
  return summary.optionNames
    .filter((n) => !usedKeys.has(n))
    .map((n) => `${n}=`);
}

/**
 * Bridge between the short-name / full-ref formats the user types
 * for `add-feature <feature>` and the OCI ref the manifest loader
 * needs. A no-op for full OCI refs; a single catalog lookup for short
 * names. Returns `undefined` for unknown short-names (no completions).
 */
async function resolveFeatureRefForCompletion(
  token: string,
): Promise<string | undefined> {
  if (REGEX.featureRef.test(token)) return token;
  const catalog = await loadComponentCatalog();
  const c = catalog.get(token);
  if (!c || c.file.category !== 'feature') return undefined;
  const f = c.file.contributes.features?.[0];
  return f?.ref;
}

// ─── Static command list (mirrors completion.ts) ──────────────────

const ALL_COMMANDS = [
  'init',
  'list-apps',
  'list-components',
  'shell',
  'open',
  'run',
  'logs',
  'start',
  'stop',
  'status',
  'check',
  'apply',
  'upgrade',
  'remove',
  'restore',
  'add-service',
  'add-language',
  'add-apt-packages',
  'add-feature',
  'add-mcp-server',
  'add-from-url',
  'add-repo',
  'add-port',
  'remove-service',
  'remove-language',
  'remove-apt-packages',
  'remove-feature',
  'remove-mcp-server',
  'remove-from-url',
  'remove-repo',
  'remove-port',
  'port',
  'tunnel',
  'share',
  'completion',
] as const;

// ─── Command specs ────────────────────────────────────────────────

// Dynamic sources — resolved live by the bash/zsh engine, and host-side
// by the pwsh script (tagged with the `kind` it dispatches on).
const containerName = dynamicSource('containerName', (ctx) =>
  listContainerNames(ctx),
);
const appCandidates = dynamicSource('app', (ctx) => listAppCandidates(ctx));
const appOrServiceCandidates = dynamicSource('appOrService', (ctx) =>
  listAppOrServiceCandidates(ctx),
);
const targetCandidates = dynamicSource('target', (ctx) =>
  listTargetCandidates(ctx),
);
const runInDirs = dynamicSource('runInDir', (ctx) => listRunInDirs(ctx));

// Static sources — catalog-derived, baked into the pwsh script at
// generation time.
const languageValues = staticSource(() => listLanguageNames());
const serviceValues = staticSource(() => listServiceNames());
const featureValues = staticSource(() => listFeatureComponents());
const mcpValues = staticSource(() => listMcpServers());
const providerValues = staticSource(() => listProviders());
const shellValues = staticSource(() => listShellNames());
const openToolValues = staticSource(() => [...OPEN_TOOLS]);

const COMMAND_SPECS: Record<string, CommandSpec> = {
  init: {
    // First positional is a FRESH name → no suggestion source, but
    // the slot exists. Once the cursor is past it (after the name +
    // space), `--with` / `--with-repo` / `--with-ports` surface as
    // flag suggestions.
    positionalCount: 1,
    flags: {
      '--with-languages': { type: 'value', values: languageValues },
      '--with-features': { type: 'value', values: featureValues },
      '--with-services': { type: 'value', values: serviceValues },
      '--with-mcp-servers': { type: 'value', values: mcpValues },
      '--with-apt-packages': { type: 'value' },
      '--with-repos': { type: 'value' },
      '--with-ports': { type: 'value' },
    },
  },
  apply: {
    positionals: [containerName],
    flags: {
      '--yes': { type: 'boolean', aliases: ['-y'] },
      '--open': { type: 'value', values: openToolValues },
    },
  },
  upgrade: {
    // First positional is a container name; the second is a version
    // string (no suggestions — versions live in the registry).
    positionals: [containerName, () => []],
    flags: { '--list': { type: 'boolean' } },
  },
  remove: {
    positionals: [containerName],
    flags: {
      '--yes': { type: 'boolean', aliases: ['-y'] },
      '--no-backup': { type: 'boolean' },
    },
  },
  shell: { positionals: [containerName] },
  open: { positionals: [containerName, openToolValues] },
  run: {
    positionals: [containerName],
    flags: {
      '--in': { type: 'value', values: runInDirs },
      '--yes': { type: 'boolean', aliases: ['-y'] },
    },
  },
  logs: {
    positionals: [containerName, appCandidates],
    flags: {
      '--target': { type: 'value', values: targetCandidates },
    },
  },
  start: {
    positionals: [containerName, appCandidates],
    flags: {
      '--target': { type: 'value', values: targetCandidates },
      '--open': { type: 'value', values: openToolValues },
    },
  },
  stop: {
    positionals: [containerName, appCandidates],
    flags: {
      '--target': { type: 'value', values: targetCandidates },
    },
  },
  status: {
    positionals: [containerName, appOrServiceCandidates],
  },
  check: { positionals: [containerName] },
  'list-apps': { positionals: [containerName] },
  'add-language': {
    positionals: [containerName, languageValues],
  },
  'add-service': {
    positionals: [containerName, serviceValues],
  },
  'add-apt-packages': {
    positionals: [containerName],
    innerArgs: () => [], // freeform package names — no useful suggestion list
  },
  'add-feature': {
    positionals: [containerName, featureValues],
    innerArgs: (ctx) => listFeatureOptionInnerArgs(ctx),
  },
  'add-mcp-server': {
    // Second positional is the catalog connector; `-- key=value` inner args
    // are the connector's own options, which have no manifest to read (a
    // connector publishes no devcontainer feature), so no suggestions there.
    positionals: [containerName, mcpValues],
    innerArgs: () => [],
  },
  'add-from-url': {
    positionals: [containerName],
    flags: { '--yes': { type: 'boolean', aliases: ['-y'] } },
  },
  'add-repo': {
    positionals: [containerName],
    flags: {
      '--path': { type: 'value' },
      '--git-name': { type: 'value' },
      '--git-email': { type: 'value' },
      '--provider': { type: 'value', values: providerValues },
    },
  },
  'add-port': {
    positionals: [containerName],
    flags: {
      '--default': { type: 'boolean' },
    },
    innerArgs: () => [],
  },
  'remove-language': {
    positionals: [containerName, languageValues],
  },
  'remove-service': {
    positionals: [containerName, serviceValues],
  },
  'remove-apt-packages': {
    positionals: [containerName],
    innerArgs: () => [],
  },
  'remove-feature': {
    positionals: [containerName, featureValues],
  },
  'remove-mcp-server': {
    // Catalog connectors as suggestions; a hand-written inline server has a
    // name only the yml knows, so it is typed out.
    positionals: [containerName, mcpValues],
  },
  'remove-from-url': { positionals: [containerName] },
  'remove-repo': { positionals: [containerName] },
  'remove-port': {
    positionals: [containerName],
    innerArgs: () => [],
  },
  port: {
    positionals: [containerName],
    flags: { '--default': { type: 'boolean' } },
    innerArgs: () => [],
  },
  tunnel: {
    positionals: [containerName, serviceValues],
    flags: {
      '--local-port': { type: 'value' },
      '--local-address': { type: 'value' },
    },
  },
  share: {
    positionals: [containerName, appCandidates],
    flags: { '--forward-ports': { type: 'value' } },
  },
  completion: {
    positionals: [shellValues],
  },
  'list-components': {},
  restore: {
    // First positional is a backup-path; no value suggestions today
    // (could plug filesystem completion later). Slot still exists so
    // Tab is silent inside it rather than offering flags prematurely.
    positionalCount: 1,
  },
};

/** Exposed for tests so the command list stays in sync with main.ts. */
export const COMPLETION_ALL_COMMANDS = ALL_COMMANDS;
export const COMPLETION_COMMAND_SPEC_KEYS = Object.keys(COMMAND_SPECS);

// ─── PowerShell static-model builder ──────────────────────────────

/**
 * A completion value slot in the pwsh model: either a baked static list,
 * a dynamic kind the pwsh script resolves host-side, or nothing (freeform
 * / unsupported → no suggestions).
 */
export type PwshValueDesc =
  | { values: string[] }
  | { kind: PwshValueKind }
  | Record<string, never>;

export interface PwshFlagDesc {
  type: 'boolean' | 'value';
  aliases: string[];
  /** Present only for value flags that carry a suggestion source. */
  value?: PwshValueDesc;
}

export interface PwshCommandDesc {
  positionals: PwshValueDesc[];
  positionalCount: number;
  flags: Record<string, PwshFlagDesc>;
}

export interface PwshCompletionModel {
  commands: string[];
  specs: Record<string, PwshCommandDesc>;
}

const EMPTY_CTX: Ctx = { prev: [], current: '', opts: {} };

async function describeSource(
  src: ValueSource | undefined,
): Promise<PwshValueDesc> {
  if (!src) return {};
  const meta = pwshMeta.get(src);
  // Untagged sources (freeform value flags, the `upgrade` version slot,
  // `() => []` inner-args) get no pwsh suggestions.
  if (!meta) return {};
  if (meta.static) return { values: await src(EMPTY_CTX) };
  return { kind: meta.kind };
}

/**
 * Derive the self-contained model the pwsh completion script embeds, from
 * the same `COMMAND_SPECS` the bash/zsh engine uses. Static value sources
 * are evaluated now (catalog lookups) and baked in; dynamic ones are
 * reduced to a `kind` the pwsh script resolves off the filesystem.
 */
export async function buildPwshCompletionModel(): Promise<PwshCompletionModel> {
  const specs: Record<string, PwshCommandDesc> = {};
  for (const [name, spec] of Object.entries(COMMAND_SPECS)) {
    const posSources = spec.positionals ?? [];
    const positionalCount = spec.positionalCount ?? posSources.length;
    const positionals: PwshValueDesc[] = [];
    for (const src of posSources) positionals.push(await describeSource(src));

    const flags: Record<string, PwshFlagDesc> = {};
    for (const [flag, fspec] of Object.entries(spec.flags ?? {})) {
      const entry: PwshFlagDesc = {
        type: fspec.type,
        aliases: fspec.aliases ?? [],
      };
      if (fspec.type === 'value' && fspec.values) {
        entry.value = await describeSource(fspec.values);
      }
      flags[flag] = entry;
    }
    specs[name] = { positionals, positionalCount, flags };
  }
  return { commands: [...ALL_COMMANDS], specs };
}
